/**
 * Factuur Formulier - Nieuw/Bewerken
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { facturenService, FactuurCreateData, FactuurRegel } from '../../services/facturen.service';
import { relationsService, Relation } from '../../services/relations.service';
import { botenService, BootDetail } from '../../services/boten.service';
import { voorraadService, VoorraadItem } from '../../services/voorraad.service';
import { bedrijfsgegevensService } from '../../services/bedrijfsgegevens.service';
import './Facturen.css';
import '../relations/Relations.css';

interface FactuurFormProps {
  factuurId?: number;
}

// Vervaldatum is geen invoerveld meer: factuurdatum + betalingstermijn.
const berekenVervaldatum = (factuurDatum: string, termijn: number): string => {
  const d = new Date(factuurDatum);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + (Number(termijn) || 0));
  return d.toISOString().split('T')[0];
};

const FactuurForm = ({ factuurId }: FactuurFormProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const actualFactuurId = factuurId || (id && id !== 'new' ? parseInt(id) : undefined);
  const isEditMode = !!actualFactuurId;

  const [formData, setFormData] = useState<FactuurCreateData>({
    relatie_id: 0,
    boot_id: undefined,
    factuur_datum: new Date().toISOString().split('T')[0],
    vervaldatum: berekenVervaldatum(new Date().toISOString().split('T')[0], 30),
    onderwerp: '',
    omschrijving: '',
    interne_notities: '',
    betalingstermijn: 30,
    betalingsvoorwaarden: '',
    btw_percentage: 21.00,
    subtotaal: 0.00,
    btw_bedrag: 0.00,
    totaal: 0.00,
    korting_percentage: 0.00,
    korting_bedrag: 0.00,
    regels: [
      {
        volgnummer: 1,
        type_regel: 'ARBEID',
        omschrijving: '',
        aantal: 1,
        eenheid: 'Uur',
        prijs_per_eenheid: 0,
        korting_percentage: 0,
        korting_bedrag: 0,
        btw_percentage: 21,
        totaal: 0
      }
    ]
  });

  const [relaties, setRelaties] = useState<Relation[]>([]);
  const [boten, setBoten] = useState<BootDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatieSearch, setRelatieSearch] = useState('');
  const [relatieDropdownOpen, setRelatieDropdownOpen] = useState(false);
  const [uurloonVerkoop, setUurloonVerkoop] = useState<number>(0);

  // Voorraad dropdown state
  const [voorraadItems, setVoorraadItems] = useState<VoorraadItem[]>([]);
  const [voorraadSearch, setVoorraadSearch] = useState<{[key: number]: string}>({});
  const [voorraadDropdownOpen, setVoorraadDropdownOpen] = useState<{[key: number]: boolean}>({});


  useEffect(() => {
    loadOptions();
    loadVoorraadItems();
    loadBedrijfsgegevens();
    if (isEditMode && actualFactuurId) {
      loadFactuur(actualFactuurId);
    }
  }, [isEditMode, actualFactuurId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-dropdown')) {
        setRelatieDropdownOpen(false);
        setVoorraadDropdownOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (formData.relatie_id > 0) {
      loadBotenVoorRelatie(formData.relatie_id);
    }
  }, [formData.relatie_id]);

  const loadOptions = async () => {
    try {
      // Laad alle relaties (mogelijk meer dan 100)
      let allRelaties: Relation[] = [];
      let relatiesOffset = 0;
      let relatiesHasMore = true;

      while (relatiesHasMore) {
        const relatiesData = await relationsService.getAll({
          actief: true,
          limit: 100,
          offset: relatiesOffset,
          type: 'Klant'
        });
        allRelaties = [...allRelaties, ...relatiesData.items];
        relatiesHasMore = relatiesData.items.length === 100;
        relatiesOffset += 100;
      }

      setRelaties(allRelaties);
    } catch (err: any) {
      setError(err.message || 'Kon opties niet laden');
    }
  };

  const loadFactuur = async (id: number) => {
    try {
      setLoading(true);
      const data = await facturenService.getById(id);

      setFormData({
        relatie_id: data.relatie_id,
        boot_id: data.boot_id,
        factuur_datum: data.factuur_datum,
        vervaldatum: data.vervaldatum,
        onderwerp: data.onderwerp,
        omschrijving: data.omschrijving || '',
        interne_notities: data.interne_notities || '',
        betalingstermijn: data.betalingstermijn,
        betalingsvoorwaarden: data.betalingsvoorwaarden || '',
        btw_percentage: data.btw_percentage,
        subtotaal: data.subtotaal,
        btw_bedrag: data.btw_bedrag,
        totaal: data.totaal,
        korting_percentage: data.korting_percentage,
        korting_bedrag: data.korting_bedrag,
        regels: data.regels && data.regels.length > 0
          ? [...data.regels, {
              volgnummer: data.regels.length + 1,
              type_regel: 'ARBEID',
              omschrijving: '',
              aantal: 1,
              eenheid: 'Uur',
              prijs_per_eenheid: 0,
              korting_percentage: 0,
              korting_bedrag: 0,
              btw_percentage: 21,
              totaal: 0
            }]
          : [{
              volgnummer: 1,
              type_regel: 'ARBEID',
              omschrijving: '',
              aantal: 1,
              eenheid: 'Uur',
              prijs_per_eenheid: 0,
              korting_percentage: 0,
              korting_bedrag: 0,
              btw_percentage: 21,
              totaal: 0
            }]
      });

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon factuur niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadBotenVoorRelatie = async (relatieId: number) => {
    try {
      const botenData = await botenService.getAll({
        eigenaar_id: relatieId,
        actief: true,
        limit: 100
      });
      setBoten(botenData);
    } catch (err: any) {
      console.error('Kon boten niet laden:', err);
      setBoten([]);
    }
  };

  const loadVoorraadItems = async () => {
    try {
      const items = await voorraadService.searchItems();
      setVoorraadItems(items);
    } catch (err: any) {
      console.error('Kon voorraad items niet laden:', err);
      setVoorraadItems([]);
    }
  };

  const loadBedrijfsgegevens = async () => {
    try {
      const gegevens = await bedrijfsgegevensService.get();
      // Converteer van centen naar euros
      setUurloonVerkoop(gegevens.uurloon_verkoop / 100);

      // Als het een nieuwe factuur is, vul standaardwaarden in
      if (!isEditMode) {
        const uurloon = gegevens.uurloon_verkoop / 100;
        setFormData(prev => {
          // Update bestaande ARBEID-regels met prijs 0 naar het uurloon
          const updatedRegels = (prev.regels || []).map(regel =>
            regel.type_regel === 'ARBEID' && (!regel.prijs_per_eenheid || regel.prijs_per_eenheid === 0)
              ? { ...regel, prijs_per_eenheid: uurloon, totaal: parseFloat((parseFloat(String(regel.aantal || 0)) * uurloon).toFixed(2)) }
              : regel
          );
          return {
            ...prev,
            regels: updatedRegels
          };
        });
      }
    } catch (err: any) {
      console.error('Kon bedrijfsgegevens niet laden:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const deleteRegel = (regelIndex: number) => {
    setFormData(prev => {
      const regels = prev.regels?.filter((_, i) => i !== regelIndex) || [];
      const totalen = berekenTotalen(regels);
      return {
        ...prev,
        regels,
        ...totalen
      };
    });
  };

  const moveRegelUp = (regelIndex: number) => {
    if (regelIndex === 0) return;

    setFormData(prev => {
      const regels = [...(prev.regels || [])];
      [regels[regelIndex - 1], regels[regelIndex]] = [regels[regelIndex], regels[regelIndex - 1]];
      regels.forEach((regel, i) => {
        regel.volgnummer = i + 1;
      });
      return {
        ...prev,
        regels
      };
    });
  };

  const moveRegelDown = (regelIndex: number) => {
    setFormData(prev => {
      const regels = [...(prev.regels || [])];
      if (regelIndex === regels.length - 1) return prev;

      [regels[regelIndex], regels[regelIndex + 1]] = [regels[regelIndex + 1], regels[regelIndex]];
      regels.forEach((regel, i) => {
        regel.volgnummer = i + 1;
      });
      return {
        ...prev,
        regels
      };
    });
  };

  const handleRegelUpdate = (regelIndex: number, field: keyof FactuurRegel, value: any) => {
    setFormData(prev => {
      const regels = JSON.parse(JSON.stringify(prev.regels || []));
      const updatedRegel = regels[regelIndex];

      if (updatedRegel) {
        (updatedRegel as any)[field] = value;

        // Als type_regel wordt gewijzigd naar ARBEID, vul automatisch het uurloon in
        if (field === 'type_regel' && value === 'ARBEID' && uurloonVerkoop > 0) {
          updatedRegel.prijs_per_eenheid = uurloonVerkoop;
          updatedRegel.eenheid = 'Uur';
        }

        // Andersom: 'Uur' bestaat niet in de eenhedenlijst van de overige types.
        // Zonder deze reset toont de dropdown Stuks, maar blijft 'Uur' opgeslagen
        // en komt dat ook op de factuur te staan.
        if (field === 'type_regel' && value !== 'ARBEID' && updatedRegel.eenheid === 'Uur') {
          updatedRegel.eenheid = 'Stuks';
        }

        // Bij lege ARBEID-regel: vul uurloon in zodra er getypt wordt
        if (updatedRegel.type_regel === 'ARBEID' && updatedRegel.prijs_per_eenheid === 0 && uurloonVerkoop > 0) {
          if (field === 'omschrijving' || field === 'aantal') {
            updatedRegel.prijs_per_eenheid = uurloonVerkoop;
            if (field === 'aantal' || !updatedRegel.aantal) {
              updatedRegel.aantal = updatedRegel.aantal || 1;
            }
          }
        }

        // Recalculate total for the updated rule
        const aantal = parseFloat(String(updatedRegel.aantal || 0));
        const prijs = parseFloat(String(updatedRegel.prijs_per_eenheid || 0));
        let totaal = aantal * prijs;

        if (updatedRegel.korting_percentage && parseFloat(String(updatedRegel.korting_percentage)) > 0) {
          totaal -= totaal * (parseFloat(String(updatedRegel.korting_percentage)) / 100);
        } else if (updatedRegel.korting_bedrag && parseFloat(String(updatedRegel.korting_bedrag)) > 0) {
          totaal -= parseFloat(String(updatedRegel.korting_bedrag));
        }
        updatedRegel.totaal = parseFloat(totaal.toFixed(2));

        // Check if a new blank row should be added
        if (
          regelIndex === regels.length - 1 && // Is this the last row?
          updatedRegel.omschrijving && updatedRegel.omschrijving.trim() !== '' // Is it no longer blank?
        ) {
          const newBlankRegel: FactuurRegel = {
            volgnummer: regels.length + 1,
            type_regel: 'ARBEID',
            omschrijving: '',
            aantal: 1,
            eenheid: 'Uur',
            prijs_per_eenheid: 0,
            korting_percentage: 0,
            korting_bedrag: 0,
            btw_percentage: 21,
            totaal: 0
          };
          regels.push(newBlankRegel);
        }
        
        const totalen = berekenTotalen(regels);
        return {
          ...prev,
          regels,
          ...totalen
        };
      }
      return prev;
    });
  };

  const berekenTotalen = (regels: FactuurRegel[]) => {
    const subtotaal = regels.reduce((sum, regel) => {
      const regelTotaal = typeof regel.totaal === 'number' ? regel.totaal : parseFloat(String(regel.totaal || 0));
      return sum + regelTotaal;
    }, 0);
    const btw_bedrag = subtotaal * ((formData.btw_percentage || 21) / 100);
    const totaal = subtotaal + btw_bedrag;

    return {
      subtotaal: parseFloat(subtotaal.toFixed(2)),
      btw_bedrag: parseFloat(btw_bedrag.toFixed(2)),
      totaal: parseFloat(totaal.toFixed(2))
    };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.relatie_id === 0) {
      setError('Selecteer een klant');
      return;
    }

    if (!formData.onderwerp) {
      setError('Voer een onderwerp in');
      return;
    }

    // Check of er minimaal één regel met inhoud zit
    const hasRegels = formData.regels?.some(regel => regel.omschrijving && regel.omschrijving.trim() !== '');
    if (!hasRegels) {
      setError('Voeg minimaal één regel toe');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Filter lege regels weg
      const filteredRegels = formData.regels?.filter(regel => 
        regel.omschrijving && regel.omschrijving.trim() !== ''
      ) || [];

      const submitData = {
        ...formData,
        vervaldatum: berekenVervaldatum(formData.factuur_datum, formData.betalingstermijn || 0),
        regels: filteredRegels
      };

      if (isEditMode) {
        await facturenService.update(actualFactuurId!, submitData);
        navigate(`/facturen/${actualFactuurId}`);
      } else {
        const newFactuur = await facturenService.create(submitData);
        navigate(`/facturen/${newFactuur.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const vervaldatum = berekenVervaldatum(formData.factuur_datum, formData.betalingstermijn || 0);

  const selectedRelatie = relaties.find(r => r.id === formData.relatie_id);
  const filteredRelaties = relatieSearch
    ? relaties.filter(r =>
        r.bedrijfsnaam.toLowerCase().includes(relatieSearch.toLowerCase()) ||
        (r.voornaam && r.voornaam.toLowerCase().includes(relatieSearch.toLowerCase())) ||
        (r.achternaam && r.achternaam.toLowerCase().includes(relatieSearch.toLowerCase()))
      )
    : relaties;

  if (loading && isEditMode) {
    return (
      <div className="factuur-form-container">
        <div className="loading">Factuur laden...</div>
      </div>
    );
  }

  return (
    <div className="factuur-form-container">
      <div className="page-header">
        <h1>{isEditMode ? 'Factuur Bewerken' : 'Nieuwe Factuur'}</h1>
        <button className="btn btn-secondary" onClick={() => navigate(isEditMode ? `/facturen/${actualFactuurId}` : '/facturen')}>
          {isEditMode ? 'Annuleren' : 'Annuleren'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>Klantgegevens</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Klant *</label>
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={relatieDropdownOpen
                    ? relatieSearch
                    : (selectedRelatie
                        ? (selectedRelatie.bedrijfsnaam === 'Particulier'
                            ? (() => {
                                const naamParts = [
                                  selectedRelatie.achternaam,
                                  selectedRelatie.tussenvoegsel ? `${selectedRelatie.voornaam} ${selectedRelatie.tussenvoegsel}` : selectedRelatie.voornaam
                                ].filter(Boolean);
                                return naamParts.join(', ');
                              })()
                            : selectedRelatie.bedrijfsnaam)
                        : relatieSearch)}
                  onChange={(e) => setRelatieSearch(e.target.value)}
                  onFocus={() => {
                    setRelatieDropdownOpen(true);
                    setRelatieSearch('');
                  }}
                  placeholder="Zoek klant..."
                  required
                  autoComplete="off"
                />
                {relatieDropdownOpen && (
                  <div className="dropdown-list">
                    {filteredRelaties.map(relatie => {
                      let displayName = relatie.bedrijfsnaam;
                      if (relatie.bedrijfsnaam === 'Particulier') {
                        const naamParts = [
                          relatie.achternaam,
                          relatie.tussenvoegsel ? `${relatie.voornaam} ${relatie.tussenvoegsel}` : relatie.voornaam
                        ].filter(Boolean);
                        displayName = naamParts.join(', ');
                      }
                      return (
                        <div
                          key={relatie.id}
                          className="dropdown-item"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, relatie_id: relatie.id }));
                            setRelatieSearch('');
                            setRelatieDropdownOpen(false);
                          }}
                        >
                          {displayName}
                        </div>
                      );
                    })}
                    {filteredRelaties.length === 0 && (
                      <div className="dropdown-item disabled">Geen klanten gevonden</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Boot (optioneel)</label>
              <select
                name="boot_id"
                value={formData.boot_id || ''}
                onChange={handleChange}
              >
                <option value="">Geen boot geselecteerd</option>
                {boten.map(boot => (
                  <option key={boot.id} value={boot.id}>
                    {boot.bootnaam || boot.boot_model.model}{boot.registratienummer ? ` (${boot.registratienummer})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Factuur Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Factuurdatum *</label>
              <input
                type="date"
                name="factuur_datum"
                value={formData.factuur_datum}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Betalingstermijn (dagen)</label>
              <input
                type="number"
                name="betalingstermijn"
                value={formData.betalingstermijn}
                onChange={handleChange}
                min="1"
                placeholder="30"
              />
            </div>

            <div className="form-group">
              <label>Vervaldatum</label>
              <div className="readonly-value">
                {vervaldatum
                  ? new Date(vervaldatum).toLocaleDateString('nl-NL')
                  : '-'}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Onderwerp *</label>
            <textarea
              name="onderwerp"
              value={formData.onderwerp}
              onChange={handleChange}
              rows={3}
              maxLength={255}
              placeholder="Bijv: Onderhoud boot 2024 - korte omschrijving van de opdracht"
              required
            />
          </div>

          <div className="form-group">
            <label>Interne Notities (niet zichtbaar voor klant)</label>
            <textarea
              name="interne_notities"
              value={formData.interne_notities}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Factuurregels</h2>

          {formData.regels && formData.regels.length > 0 && (
            <div className="regels-list">
              <table className="regels-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th style={{ width: '400px' }}>Omschrijving</th>
                    <th style={{ width: '80px' }}>Aantal</th>
                    <th>Eenheid</th>
                    <th style={{ width: '100px' }}>Prijs</th>
                    <th>Totaal</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.regels.map((regel, index) => (
                    <tr key={index}>
                      <td>{regel.volgnummer}</td>
                      <td>
                        <select
                          name="type_regel"
                          value={regel.type_regel}
                          onChange={(e) => handleRegelUpdate(index, 'type_regel', e.target.value)}
                          className="form-control-inline"
                          style={{ border: 'none', outline: 'none' }}
                        >
                          <option value="ARBEID">Arbeid</option>
                          <option value="ONDERDEEL">Voorraad</option>
                          <option value="UITBESTEED">Uitbesteed</option>
                          <option value="TEKST">Anders</option>
                        </select>
                      </td>
                      <td style={{ width: '400px' }}>
                        {regel.type_regel === 'ONDERDEEL' ? (
                          <div className="searchable-dropdown" style={{ position: 'relative', width: '100%' }}>
                            <input
                              type="text"
                              placeholder="Zoek voorraad item..."
                              value={voorraadSearch[index] !== undefined ? voorraadSearch[index] : regel.omschrijving}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setVoorraadSearch(prev => ({ ...prev, [index]: newValue }));
                                handleRegelUpdate(index, 'omschrijving', newValue);
                                setVoorraadDropdownOpen(prev => ({ ...prev, [index]: true }));
                              }}
                              onFocus={() => setVoorraadDropdownOpen(prev => ({ ...prev, [index]: true }))}
                              className="form-control-inline"
                              style={{ width: '100%', border: 'none', outline: 'none' }}
                              autoComplete="off"
                            />
                            {voorraadDropdownOpen[index] && (
                              <div className="dropdown-list" style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                maxHeight: '300px',
                                overflowY: 'auto',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                zIndex: 1000,
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                              }}>
                                {voorraadItems
                                  .filter(item => {
                                    const search = (voorraadSearch[index] || '').toLowerCase();
                                    if (!search) return true;

                                    return (item.onderdeel_type || '').toLowerCase().includes(search) ||
                                      (item.model || '').toLowerCase().includes(search);
                                  })
                                  .slice(0, 500)
                                  .map(item => (
                                    <div
                                      key={item.id}
                                      onClick={() => {
                                        const displayText = `${item.onderdeel_type || ''} ${item.model || ''}`.trim();
                                        handleRegelUpdate(index, 'omschrijving', displayText);
                                        handleRegelUpdate(index, 'voorraad_item_id', item.id);
                                        if (item.verkoopprijs) {
                                          handleRegelUpdate(index, 'prijs_per_eenheid', item.verkoopprijs);
                                        }
                                        setVoorraadSearch(prev => ({ ...prev, [index]: displayText }));
                                        setVoorraadDropdownOpen(prev => ({ ...prev, [index]: false }));
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    >
                                      {item.onderdeel_type || ''} {item.model || ''}
                                    </div>
                                  ))}
                                {voorraadItems.filter(item => {
                                  const search = (voorraadSearch[index] || '').toLowerCase();
                                  if (!search) return true;

                                  return (item.onderdeel_type || '').toLowerCase().includes(search) ||
                                    (item.model || '').toLowerCase().includes(search);
                                }).length === 0 && (
                                  <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                                    Geen items gevonden
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <input
                            type="text"
                            name="omschrijving"
                            value={regel.omschrijving}
                            onChange={(e) => handleRegelUpdate(index, 'omschrijving', e.target.value)}
                            className="form-control-inline"
                            style={{ width: '100%', border: 'none', outline: 'none' }}
                          />
                        )}
                      </td>
                      <td style={{ width: '80px' }}>
                        <input
                          type="number"
                          name="aantal"
                          value={regel.aantal}
                          onChange={(e) => handleRegelUpdate(index, 'aantal', parseFloat(e.target.value))}
                          step="0.01"
                          min="0"
                          className="form-control-inline"
                          style={{ width: '70px', border: 'none', outline: 'none' }}
                        />
                      </td>
                      <td>
                        <select
                          name="eenheid"
                          value={regel.eenheid}
                          onChange={(e) => handleRegelUpdate(index, 'eenheid', e.target.value)}
                          disabled={regel.type_regel === 'ARBEID'}
                          className="form-control-inline"
                          style={{ border: 'none', outline: 'none' }}
                        >
                          {regel.type_regel === 'ARBEID' ? (
                            <option value="Uur">Uur</option>
                          ) : (
                            <>
                              <option value="Stuks">Stuks</option>
                              <option value="Liter">Liter</option>
                              <option value="Meter">Meter</option>
                              <option value="m²">m²</option>
                              <option value="kg">kg</option>
                              <option value="Set">Set</option>
                              <option value="Pak">Pak</option>
                              <option value="Doos">Doos</option>
                            </>
                          )}
                        </select>
                      </td>
                      <td style={{ width: '100px' }}>
                        <input
                          type="number"
                          name="prijs_per_eenheid"
                          value={regel.prijs_per_eenheid}
                          onChange={(e) => handleRegelUpdate(index, 'prijs_per_eenheid', parseFloat(e.target.value))}
                          step="0.01"
                          min="0"
                          className="form-control-inline"
                          style={{ width: '90px', border: 'none', outline: 'none' }}
                        />
                      </td>
                      <td><strong>{formatCurrency(regel.totaal)}</strong></td>
                      <td>
                        <div className="regel-actions">
                          <div className="move-buttons">
                            <button
                              type="button"
                              className="btn-move-up"
                              onClick={() => moveRegelUp(index)}
                              disabled={index === 0}
                              title="Omhoog"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="btn-move-down"
                              onClick={() => moveRegelDown(index)}
                              disabled={index === (formData.regels?.length || 0) - 1}
                              title="Omlaag"
                            >
                              ↓
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteRegel(index)}
                          >
                            Verwijderen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="form-section totalen-section">
          <div className="totalen">
            <div className="totaal-row">
              <span>Subtotaal:</span>
              <strong>{formatCurrency(formData.subtotaal || 0)}</strong>
            </div>
            <div className="totaal-row">
              <span>BTW ({formData.btw_percentage}%):</span>
              <strong>{formatCurrency(formData.btw_bedrag || 0)}</strong>
            </div>
            <div className="totaal-row totaal-final">
              <span>Totaal:</span>
              <strong>{formatCurrency(formData.totaal || 0)}</strong>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/facturen')}>
            Annuleren
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Opslaan...' : (isEditMode ? 'Factuur Bijwerken' : 'Factuur Aanmaken')}
          </button>
        </div>
      </form>

    </div>
  );
};

export default FactuurForm;
